import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import H2 from 'components/H2';
import { Flex, Box, Button, Heading } from 'rebass';
import { Input, Label } from '@rebass/forms';
import config from 'config';
import { FcGoogle } from 'react-icons/fc';
import NiceWrapper from 'components/NiceWrapper';
import services from 'services/services';
import auth from 'services/auth';
import { MarketingPageWrapper, MarketingHeader } from 'components/Marketing';
import { withToastManager } from 'react-toast-notifications';
import EventEmitter from 'utils/events';
import { hotjar } from 'react-hotjar';
import helpers from 'utils/helpers';

class LoginPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            name: '',
            email: '',
            password: '',
            two_fa_code: null,
            loadingEmailAuth: false,
            isSignUpPage: this.props.match.path == '/signup',
            viaEmail: true
        };

        this.loginViaEmail = this.loginViaEmail.bind(this);
        this.createViaEmail = this.createViaEmail.bind(this);
        this.dispatchAuthEvent = this.dispatchAuthEvent.bind(this);
    }

    loginGoogle() {
        auth.clearStorage();
        const baseURL = config.API_URL;
        const browser_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        window.location = `${baseURL}/login-provider/google?timezone=${browser_timezone}`;
    }

    loginAzure() {
        auth.clearStorage();
        const baseURL = config.API_URL;
        window.location = `${baseURL}/login-provider/azure`;
    }

    dispatchAuthEvent() {
        EventEmitter.dispatch('auth.update');
    }

    loginViaEmail() {
        const { toastManager } = this.props;
        const that = this;
        this.setState({ loadingEmailAuth: true });

        auth.loginAccountEmail(this.state.email, this.state.password, this.state.two_fa_code)
            .then((response) => {
                if (response && response.sent_two_fa) {
                    const two_fa_code = prompt('Please enter the 2FA code we sent to your phone via SMS:');
                    if (two_fa_code) {
                        this.setState({two_fa_code}, () => {
                            that.loginViaEmail();
                        });
                    } else {
                        this.setState({ loadingEmailAuth: false, two_fa_code: null });
                    }
                } else {
                    this.dispatchAuthEvent();
                    this.setState({ loadingEmailAuth: false, two_fa_code: null });
                }
            })
            .catch(error => {
                //console.log(error);
                this.setState({ loadingEmailAuth: false, two_fa_code: null });
                const errorText = services.parseXhrErrors(error);

                toastManager.add(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    createViaEmail(e) {
        const { toastManager } = this.props;
        e.preventDefault();
        this.setState({ loadingEmailAuth: true });

        // timezone
        const browser_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        auth.createAccountEmail(this.state.email, this.state.password, this.state.name, browser_timezone)
            .then(response => {

                helpers.googleTagManagerEventSignup();

                this.dispatchAuthEvent();
                this.setState({ loadingEmailAuth: false });
            })
            .catch(error => {
                console.log(error);
                this.setState({ loadingEmailAuth: false });
                const errorText = services.parseXhrErrors(error);

                toastManager.add(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    componentDidMount () {
        if (this.state.isSignUpPage) {
            console.log('LOADING HOTJAR')
            // initialize hotjar
            const hjid = 1515422;
            const hjsv = 6;
            hotjar.initialize(hjid, hjsv);
        }
    }

    render() {
        return (
            <MarketingPageWrapper>
                <Helmet>
                    <title>Sign in</title>
                </Helmet>

                <MarketingHeader auth={this.props.auth} />

                <Box
                    sx={{
                        mx: 'auto',
                        marginTop: '80px',
                        textAlign: 'center'
                    }}
                >
                    <Heading mb={3}>{this.state.isSignUpPage ? 'Welcome to Panelfox' : 'Welcome back'}</Heading>
                    <NiceWrapper style={{ textAlign: 'center', padding: '32px', background: '#fff' }}>
                        <Flex flexDirection="column">
                            <Button variant="secondary-gray" className="huge" sx={{ marginRight: 0 }} onClick={this.loginGoogle}>
                                <FcGoogle style={{ marginRight: '8px', fontSize: '26px', marginTop: '-2px' }} />{' '}
                                {this.state.isSignUpPage ? 'Sign up ' : 'Sign in '}
                                with Google
                            </Button>

                            <Button variant="secondary-gray" className="huge" mr={0} mt={3} onClick={this.loginAzure}>
                                <img
                                    src="/microsoft.png"
                                    style={{ width: '16px', height: '16px', verticalAlign: 'top', margin: '4px 14px 0 0' }}
                                    alt="Microsoft"
                                />
                                {this.state.isSignUpPage ? 'Sign up ' : 'Sign in '} with Microsoft
                            </Button>
                        </Flex>

                        {this.state.viaEmail ? (
                            <>
                                <br />
                                <hr />
                                <Box mt={3} sx={{ fontSize: '13px', fontWeight: 400 }}>
                                    You can also sign {this.state.isSignUpPage ? 'up' : 'in'} with your email.
                                </Box>

                                {this.state.isSignUpPage ? (
                                    <form onSubmit={this.createViaEmail}>
                                        <Input
                                            type="text"
                                            name="name"
                                            required
                                            autoComplete="off"
                                            placeholder="Full Name"
                                            mt={3}
                                            value={this.state.name}
                                            onChange={e => this.setState({ name: e.target.value })}
                                        />
                                        <Input
                                            type="email"
                                            name="email"
                                            required
                                            autoComplete="off"
                                            placeholder="Email"
                                            mt={3}
                                            value={this.state.email}
                                            onChange={e => this.setState({ email: e.target.value })}
                                        />
                                        <Input
                                            type="password"
                                            name="password"
                                            required
                                            autoComplete="off"
                                            placeholder="Password"
                                            mt={3}
                                            value={this.state.password}
                                            onChange={e => this.setState({ password: e.target.value })}
                                        />
                                        <Button
                                            type="submit"
                                            variant="tertiary"
                                            mt={3}
                                            width="100%"
                                            disabled={this.state.loadingEmailAuth}
                                        >
                                            {this.state.loadingEmailAuth ? 'Signing up...' : 'Sign up with Email'}
                                        </Button>
                                    </form>
                                ) : (
                                    <form onSubmit={(e) => { e.preventDefault(); this.loginViaEmail(); }}>
                                        <Input
                                            type="email"
                                            name="email"
                                            required
                                            placeholder="Email"
                                            mt={3}
                                            value={this.state.email}
                                            onChange={e => this.setState({ email: e.target.value })}
                                        />
                                        <Input
                                            type="password"
                                            name="password"
                                            required
                                            placeholder="Password"
                                            mt={3}
                                            value={this.state.password}
                                            onChange={e => this.setState({ password: e.target.value })}
                                        />
                                        <Button
                                            type="submit"
                                            variant="tertiary"
                                            mt={3}
                                            width="100%"
                                            disabled={this.state.loadingEmailAuth}
                                        >
                                            {this.state.loadingEmailAuth ? 'Signing In...' : 'Sign In with Email'}
                                        </Button>
                                    </form>
                                )}
                            </>
                        ) : <></>}
                    </NiceWrapper>
                    {this.state.isSignUpPage == false &&
                        <Link to="/forgot-password">
                            <Button
                                type="submit"
                                variant="link"
                                className='muted'
                                mt={4}
                                width="100%"
                                fontSize={1}
                            >
                                Forgot password
                            </Button>
                        </Link>
                    }
                    {this.state.isSignUpPage && (
                        <div style={{ margin: '24px 0 0 0', color: '#999', fontSize: '13px' }}>
                            By signing up, you agree to <Link to="/docs/legal/terms">Terms</Link> &{' '}
                            <Link to="/docs/legal/privacy">Privacy</Link>.
                        </div>
                    )}
                    <br />
                </Box>
            </MarketingPageWrapper>
        );
    }
}

export default withToastManager(LoginPage);
